/**
 * TagPeople - the "Tag people" multi-select every task form shows next to its
 * owner picker (Sharif, 10/6/26). A task keeps ONE owner; the people tagged
 * here are the others involved. They see the task under "Mine" and get an
 * email when they are tagged (server/lib/taskEmails.js).
 *
 * Type to filter the users, Up / Down to move, Enter (or Tab on a typed name)
 * to add, Backspace in the empty box to take the last one off, Escape to
 * close the list. Each chosen person is a chip with its own remove button.
 * The owner is never offered (an owner is never also tagged) and a person
 * already chosen is not offered twice.
 *
 * Also here: TaggedNames and TaggedChip, the read-only display the task lists
 * use (rules in ./tagged.js).
 */
import { useId, useMemo, useState } from 'react';
import { TAGGED_MAX, tagSuggestions, taggedShort, taggedTitle } from './tagged';

const FONT = 'Montserrat';
const chipSt = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '1px 3px 1px 8px',
  borderRadius: 10,
  background: '#EEF3FB',
  color: '#1F4E8C',
  fontSize: 10.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  lineHeight: '17px',
};
const removeSt = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#5B7BA6',
  fontSize: 12,
  lineHeight: '12px',
  padding: '0 4px',
  fontFamily: FONT,
  borderRadius: 8,
};

export default function TagPeople({ value, onChange, people, owner = '', style, inputStyle, label = 'Tag people', id }) {
  const tagged = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const auto = useId();
  const inputId = id || `tag-people-${auto}`;
  const listId = `${inputId}-list`;
  const full = tagged.length >= TAGGED_MAX;
  const matches = useMemo(() => (full ? [] : tagSuggestions(people, { owner, tagged, query })), [full, people, owner, tagged, query]);
  const showList = open && matches.length > 0;
  const at = Math.min(active, Math.max(matches.length - 1, 0));

  const add = (name) => {
    if (!name || full) return;
    onChange([...tagged, name]);
    setQuery('');
    setActive(0);
  };
  const remove = (name) => onChange(tagged.filter((n) => n !== name));

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && showList) {
      // Enter picks; it never submits the form underneath while the list is open
      e.preventDefault();
      e.stopPropagation();
      add(matches[at]);
    } else if (e.key === 'Tab' && showList && query.trim()) {
      add(matches[at]);
    } else if (e.key === 'Backspace' && !query && tagged.length) {
      remove(tagged[tagged.length - 1]);
    } else if (e.key === 'Escape' && showList) {
      // close the list only: the form (or dialog) around it stays open
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div style={{ position: 'relative', minWidth: 0, ...style }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 4,
          padding: '3px 6px',
          minHeight: 30,
          border: '1px solid #DDD',
          borderRadius: 6,
          background: '#fff',
          boxSizing: 'border-box',
          ...inputStyle,
        }}
      >
        {tagged.map((n) => (
          <span key={n} style={chipSt}>
            {n}
            <button type="button" aria-label={`Remove ${n}`} title={`Remove ${n}`} onClick={() => remove(n)} style={removeSt}>
              ×
            </button>
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-label={label}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? `${listId}-${at}` : undefined}
          value={query}
          disabled={full}
          placeholder={full ? `${TAGGED_MAX} tagged (the most)` : tagged.length ? 'Tag more…' : 'Tag people (optional)…'}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          style={{
            flex: '1 1 90px',
            minWidth: 90,
            border: 'none',
            outline: 'none',
            padding: '3px 2px',
            fontSize: 11.5,
            fontFamily: FONT,
            background: 'transparent',
            color: '#0D0D0D',
          }}
        />
      </div>
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={`${label}: people`}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 45,
            margin: '2px 0 0',
            padding: 0,
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #DDD',
            borderRadius: 8,
            minWidth: 190,
            maxHeight: 220,
            overflowY: 'auto',
            boxShadow: '0 8px 22px rgba(0,0,0,0.13)',
          }}
        >
          {matches.map((n, i) => (
            <li
              key={n}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === at}
              onMouseDown={(e) => {
                e.preventDefault();
                add(n);
              }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '7px 11px',
                cursor: 'pointer',
                fontSize: 11.5,
                fontFamily: FONT,
                color: '#0D0D0D',
                background: i === at ? '#EEF3FB' : '#fff',
              }}
            >
              {n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The tagged people in a task list, compact: "+ Paul Sarantos +2" with every
 * name in the tooltip. Nothing when nobody is tagged. `full` lists them all
 * ("Tagged: A, B") for the roomier lists.
 */
export function TaggedNames({ names, full = false, style }) {
  const list = Array.isArray(names) ? names : [];
  if (!list.length) return null;
  const title = taggedTitle(list);
  return (
    <span title={title} style={{ color: '#1F4E8C', fontWeight: 600, whiteSpace: 'nowrap', ...style }}>
      {full ? title : `+ ${taggedShort(list)}`}
    </span>
  );
}

/** "Tagged": this task is in my list because someone tagged me on it, not because I own it. */
export function TaggedChip({ style }) {
  return (
    <span
      title="You are tagged on this task (someone else owns it)"
      style={{
        fontSize: 8.5,
        fontWeight: 800,
        color: '#1F4E8C',
        background: '#EEF3FB',
        borderRadius: 8,
        padding: '1px 6px',
        whiteSpace: 'nowrap',
        textTransform: 'none',
        ...style,
      }}
    >
      Tagged
    </span>
  );
}
